<script setup lang="ts">
import { ref, watch } from 'vue';
import BottomSheetDialog from '@/components/Utilities/BottomSheetDialog.vue';
import StatusFilterOptions from '@/components/Utilities/StatusFilterOptions.vue';

const props = defineProps<{
    filterOptions: { header: string, options: string[] }[]
    tableLoading: boolean
}>();

const selectedFilters = ref<string[]>([]);

// Allows the button label to update when filters are applied
const submittedFilters = ref<string[]>([]);
const emit = defineEmits<{(event: 'apply-filters', filters: string[]): void }>();

// Mobile dialog state
const isMobileDialogOpen = ref(false);

// emit a clear filter event if the table is loading
watch(() => props.tableLoading, () => {
    if (props.tableLoading) {
        selectedFilters.value = [];
        submittedFilters.value = [];
        emit('apply-filters', []);
    }
});

const submitFilters = () => {
    emit('apply-filters', selectedFilters.value);
    submittedFilters.value = selectedFilters.value;
};

const onCancel = () => {
    selectedFilters.value = submittedFilters.value;
};

const clearAllFilters = () => {
    selectedFilters.value = [];
    submitFilters();
};

</script>
<template>
    <q-btn
        class="status-filter-button q-pa-sm"
        color="fsum-light-blue-2"
        icon="filter_alt"
        dense
        flat
        aria-label="status filter"
        tabindex="0"
        :label="submittedFilters.length > 0 ? '(' + submittedFilters.length + ')' : ''"
        @click="$q.screen.xs && (isMobileDialogOpen = true)"
    >
        <!-- Filter menu for desktop -->
        <q-menu
            v-if="!$q.screen.xs"
            class="status-filter-menu"
            @before-hide="onCancel"
        >
            <div class="row q-gutter-x-md q-gutter-y-md q-py-sm">
                <StatusFilterOptions
                    :filter-options="filterOptions"
                    :selected-filters="selectedFilters"
                    @update:selected-filters="selectedFilters = $event"
                />
            </div>

            <q-separator
                horizontal
                spaced="sm"
                :inset="false"
            />

            <div class="row justify-between button-group q-px-lg q-py-md">
                <div>
                    <q-btn
                        v-close-popup="true"
                        label="Clear All"
                        class="anchor-button q-pr-md"
                        color="fsum-med-blue"
                        :ripple="false"
                        no-caps
                        dense
                        flat
                        @click="selectedFilters = []; submitFilters()"
                    />
                </div>
                <div class="q-gutter-x-sm">
                    <q-btn
                        v-close-popup="true"
                        label="Cancel"
                        class="fsum-button-outlined q-px-sm"
                        no-caps
                        dense
                        flat
                        @click="onCancel"
                    />
                    <q-btn
                        v-close-popup="true"
                        label="Apply"
                        class="fsum-button-dark-blue q-px-sm"
                        no-caps
                        dense
                        flat
                        @click="() => submitFilters()"
                    />
                </div>
            </div>
        </q-menu>

        <!-- Filter menu for mobile -->
        <BottomSheetDialog
            v-if="$q.screen.xs"
            v-model="isMobileDialogOpen"
            title="Filter By"
            :enable-clear-all-btn="true"
            :enable-action-btns="true"
            @clear-all="clearAllFilters"
            @cancel="onCancel"
            @apply="submitFilters"
        >
            <div class="column q-gutter-y-md q-py-sm">
                <StatusFilterOptions
                    :filter-options="filterOptions"
                    :selected-filters="selectedFilters"
                    @update:selected-filters="selectedFilters = $event"
                />
            </div>
        </BottomSheetDialog>
    </q-btn>
</template>
<style scoped lang="scss">
.filter-item {
    font-size: 13px;
}

/** Customize the filter button's outline. I had trouble customizing it with the outline prop */
.status-filter-button {
    max-height: 40px;
    border: 2px solid currentColor;
}

/* Change the checkbox color */
:deep(.q-checkbox__bg) {
    border-color: var(--fsum-blue3);
}
:deep(.q-checkbox__inner.q-checkbox__inner--truthy) {
    color: var(--fsum-blue3);
}

/** Make the icon and text black */
:deep(.q-btn__content i.q-icon) {
    font-size: 20px;
    color: var(--fsum-black);
}
:deep(i.q-icon + span.block) {
    color: var(--fsum-black);
}
</style>
<style lang="scss">
.status-filter-button .on-left {
    margin-right: 2px;
}
.status-filter-menu {
    max-width: 320px !important;
}
</style>
